var _a, _b;
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { apiHolder, apis, inWindow } from 'api';
import { USER_DEFINED_MENU_LABELS, WAIMAO_PRODUCT_VERSIONS } from '@web-common/conf/waimao/constant';
import { ALL_HIDDEN_MENU_LABEL_MAP, HIDDEN_MENU_LABEL_MAP, HIDDEN_MENU_LABEL_MAP_3, AI_STFF_HIDDEN_MENU_LABEL_MAP, checkHasAiTeam } from '@web-common/utils/utils';
const roleApi = apiHolder.api.requireLogicalApi(apis.edmRoleApiImpl);
const systemApi = apiHolder.api.getSystemApi();
const storageApi = apiHolder.api.getDataStoreApi();
const enableFastMail = (_b = (_a = systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.prop) === null || _b === void 0 ? void 0 : _b.enable_fastmail;
const initState = {
    enableFastMail: enableFastMail ? enableFastMail === 'true' : undefined,
    roles: [],
    roleList: [],
    menus: [],
    menuReady: false,
    visibleMenuLabels: {},
    visibleMenuLabelsUserDefined: {},
    modules: {},
    loading: false,
    version: undefined,
    versionName: '',
    productMenus: [],
    moduleAccessRange: {},
    moduleAccessIds: {},
    ultimateVersion: false,
    exaltedVersion: false,
    navigateVersion: undefined,
};
function retryReq(asyncFunc, retryTimes, intervalFn) {
    let count = 0;
    const req = (resolve, reject) => {
        console.log('retryReq', count);
        asyncFunc()
            .then(data => {
            resolve(data);
        })
            .catch(err => {
            count++;
            if (count > retryTimes) {
                reject(err);
                console.error(`retryReq has retried ${retryTimes} times`);
                return;
            }
            setTimeout(() => req(resolve, reject), intervalFn(count));
        });
    };
    return function () {
        return new Promise((resolve, reject) => {
            req(resolve, reject);
        });
    };
}
function retryReqAndFallbackStorage(asyncFunc, retryTimes, intervalFn, key) {
    const doReq = retryReq(asyncFunc, retryTimes, intervalFn);
    const getFromStorage = (key) => {
        const str = storageApi.getSync(key, {
            noneUserRelated: false,
        }).data;
        if (str) {
            try {
                return JSON.parse(str).data;
            }
            catch (e) {
                return undefined;
            }
        }
    };
    return doReq()
        .then(res => {
        storageApi.put(key, JSON.stringify({
            data: res,
            time: +new Date(),
        }), {
            noneUserRelated: false,
        });
        return res;
    })
        .catch(err => {
        const fallbackValue = getFromStorage(key);
        console.warn(err);
        if (fallbackValue === undefined) {
            throw new Error(`请求重试${retryTimes}次失败`);
        }
        return fallbackValue;
    });
}
export const isEnableFastmailAsync = createAsyncThunk('product/privilege', async () => {
    const productId = 'fastmail';
    const doReq = () => {
        return roleApi.getProductPrivilege('fastmail').then(flag => {
            storageApi.setUserProp(`enable_${productId}`, flag ? 'true' : 'false', true);
            return flag;
        });
    };
    const wrapRetryReq = retryReq(doReq, 10, count => count * 1000);
    return wrapRetryReq();
});
export const getPrivilegeAsync = createAsyncThunk('privileges/getAll', async () => {
    const key = 'wm_privileges_all';
    return retryReqAndFallbackStorage(() => roleApi.getCurrentPrivilege(), 3, () => 2000, key);
});
export const getVersionAsync = createAsyncThunk('privileges/getVersion', async () => {
    return roleApi.getVersion({
        productId: 'fastmail',
        productVersionId: 'professional',
    });
});
export const getIsEdmAdmin = createSelector((state) => {
    if (!Array.isArray(state === null || state === void 0 ? void 0 : state.roles) || state.roles.length === 0) {
        return false;
    }
    return ((state === null || state === void 0 ? void 0 : state.roles) || []).some(role => role.roleType === 'ADMIN');
}, (isEdmAdmin) => isEdmAdmin);
export const getIsEdmManager = createSelector((state) => {
    if (!Array.isArray(state === null || state === void 0 ? void 0 : state.roles) || state.roles.length === 0) {
        return false;
    }
    return ((state === null || state === void 0 ? void 0 : state.roles) || []).some(role => role.roleType === 'SYSTEM' && role.roleName === '团队经理');
}, (isEdmManager) => isEdmManager);
export const getIsFreeVersionUser = createSelector((state) => {
    const version = state === null || state === void 0 ? void 0 : state.version;
    return version === 'FREE';
}, (isFreeVersionUser) => isFreeVersionUser);
export const getIsPayVersionUser = createSelector((state) => {
    return state.version === WAIMAO_PRODUCT_VERSIONS.FASTMAIL || state.version === WAIMAO_PRODUCT_VERSIONS.FASTMAIL_AND_WEBSITE;
}, (isPayVersionUser) => isPayVersionUser);
export const getMyRolesAsync = createAsyncThunk('roles/getAll', () => {
    return roleApi.getRoleList().catch(err => {
        var _a, _b, _c;
        if (err === '没有操作权限') {
            const accId = (_c = (_b = (_a = systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.contact) === null || _b === void 0 ? void 0 : _b.contact) === null || _c === void 0 ? void 0 : _c.id;
            const orgId = systemApi.getCurrentCompanyId();
            if (accId && orgId) {
                return roleApi.getCurrentRoleInfo({ accId, orgId: orgId ? String(orgId) : undefined }).then(myRoles => ({
                    myRoles,
                    roles: [],
                }));
            }
            return {
                myRoles: [],
                roles: [],
            };
        }
        return Promise.reject(err);
    });
});
let hasSetData = false;
export const getMenuSettingsAsync = createAsyncThunk('menus/getAll', async (conf) => {
    const key = 'wm_menu_setting';
    return retryReqAndFallbackStorage(() => {
        var _a;
        if (!hasSetData) {
            hasSetData = ((_a = storageApi.getSync('setMenuNew')) === null || _a === void 0 ? void 0 : _a.data) === '1';
        }
        const setReq = hasSetData
            ? () => Promise.resolve()
            : () => roleApi
                .setMenuListNew({
                menuVersion: 'NEW',
            })
                .then(() => {
                storageApi.put('setMenuNew', '1');
            })
                .catch(e => {
                console.error('setMenuListNew error in getMenuSettingsAsync', e);
            });
        return setReq().then(() => roleApi.getMenuListNew(conf).then(res => res === null || res === void 0 ? void 0 : res.menuItems));
    }, 3, () => 2000, key);
});
export const getModuleDataPrivilegeAsync = createAsyncThunk('module/data/privilege', async (resourceLabel) => {
    return roleApi.getModuleDataRange(resourceLabel);
});
const privilegeSlice = createSlice({
    name: 'privilegeReducer',
    initialState: initState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(getPrivilegeAsync.pending, state => {
            state.loading = true;
        })
            .addCase(getPrivilegeAsync.rejected, state => {
            state.loading = false;
        })
            .addCase(getPrivilegeAsync.fulfilled, (state, action) => {
            state.modules = groupByModule(action.payload);
            state.loading = false;
        });
        builder
            .addCase(getMyRolesAsync.fulfilled, (state, action) => {
            state.roles = action.payload.myRoles;
            state.roleList = action.payload.roles;
        })
            .addCase(getMenuSettingsAsync.fulfilled, (state, action) => {
            const allHiddenMenuLabelMap = state.ultimateVersion || state.exaltedVersion ? ALL_HIDDEN_MENU_LABEL_MAP : Object.assign(Object.assign({}, ALL_HIDDEN_MENU_LABEL_MAP), HIDDEN_MENU_LABEL_MAP_3);
            state.menus = action.payload;
            state.menuReady = true;
            const bool = checkHasAiTeam(action.payload);
            const allHiddenMenuLabelMap1 = bool
                ? Object.assign(Object.assign({}, allHiddenMenuLabelMap), AI_STFF_HIDDEN_MENU_LABEL_MAP) : allHiddenMenuLabelMap;
            state.visibleMenuLabels = buildMap(action.payload, undefined, allHiddenMenuLabelMap1);
            state.visibleMenuLabelsUserDefined = buildUserDefinedMap(action.payload);
        })
            .addCase(getModuleDataPrivilegeAsync.fulfilled, (state, action) => {
            state.moduleAccessRange[action.payload.resourceLabel] = action.payload.accessRangeList;
            state.moduleAccessIds[action.payload.resourceLabel] = action.payload.accIds || [];
        });
        builder.addCase(isEnableFastmailAsync.fulfilled, (state, action) => {
            state.enableFastMail = action.payload;
        });
        builder.addCase(getVersionAsync.fulfilled, (state, action) => {
            state.version = action.payload.version;
            state.versionName = action.payload.versionName;
            state.productMenus = action.payload.productMenus;
            state.ultimateVersion = action.payload.ultimateVersion;
            state.exaltedVersion = action.payload.exaltedVersion;
            state.navigateVersion = action.payload.navigateVersion;
        });
    },
});
export const getModuleAccessSelector = createSelector((state, resourceLabel, accessLabel) => {
    if ((resourceLabel === 'ADDRESS_BOOK' && accessLabel !== 'EXPORT') || (resourceLabel === 'ADDRESS_OPEN_SEA' && accessLabel !== 'EXPORT')) {
        return true;
    }
    const privilege = state.modules[resourceLabel];
    if (!privilege) {
        return false;
    }
    if (accessLabel.indexOf('|') > -1) {
        let labels = accessLabel.split('|');
        return labels.some(key => privilege.funcPrivileges.some(i => i.accessLabel === key));
    }
    let labels = accessLabel.split('+');
    if (labels.length === 1) {
        return privilege.funcPrivileges.some(i => i.accessLabel === accessLabel);
    }
    else {
        return labels.every(key => privilege.funcPrivileges.some(i => i.accessLabel === key));
    }
}, (hasPermisson) => hasPermisson);
export const getIsSomeMenuVisbleSelector = createSelector((state, moduleLabels) => {
    if (state && !state.menuReady) {
        return true;
    }
    return moduleLabels === null || moduleLabels === void 0 ? void 0 : moduleLabels.some(label => state && state.visibleMenuLabels[label] === true);
}, (visible) => visible);
export const isOwnerDataPrivilegeSelector = createSelector((state, resourceLabel) => {
    return state.moduleAccessRange[resourceLabel] && state.moduleAccessRange[resourceLabel].every(accessRange => accessRange === 'OWNER');
}, (flag) => flag);
export const privilegeActions = privilegeSlice.actions;
export default privilegeSlice.reducer;
function groupByModule(privileges) {
    const groups = {};
    privileges.forEach(item => {
        groups[item.resourceLabel] = {
            resourceLabel: item.resourceLabel,
            resourceName: item.resourceName,
            dataPrivilege: item.accessList.find(i => i.accessType === 'DATA'),
            funcPrivileges: item.accessList.filter(i => i.accessType !== 'DATA'),
        };
    });
    return groups;
}
const buildMap = (menus, map, hiddenLabelMap) => {
    var _a;
    map = map || { WORKBENCH: false };
    for (let i = 0, l = menus.length; i < l; i++) {
        const menu = menus[i];
        let _showMenu = menu.showMenu;
        if ((_a = menu.subMenuItems) === null || _a === void 0 ? void 0 : _a.length) {
            buildMap(menu.subMenuItems, map, hiddenLabelMap);
            if (menu.subMenuItems.some(v => hiddenLabelMap[v.menuLabel])) {
                _showMenu = _showMenu && menu.subMenuItems.some(v => !hiddenLabelMap[v.menuLabel] && v.showMenu);
            }
        }
        map[menu.menuLabel] = hiddenLabelMap[menu.menuLabel] ? false : !!_showMenu;
    }
    return map;
};
const buildUserDefinedMap = (menus, map, level = 1, userDefined = false) => {
    var _a;
    map = map || { WORKBENCH: false };
    for (let i = 0, l = menus.length; i < l; i++) {
        const menu = menus[i];
        let _userDefined = level == 1 ? USER_DEFINED_MENU_LABELS[menu.menuLabel] : userDefined;
        if ((_a = menu.subMenuItems) === null || _a === void 0 ? void 0 : _a.length) {
            buildUserDefinedMap(menu.subMenuItems, map, level + 1, _userDefined);
        }
        if (!_userDefined) {
            map[menu.menuLabel] = HIDDEN_MENU_LABEL_MAP[menu.menuLabel] ? false : !!menu.showMenu;
        }
        else {
            map[menu.menuLabel] = HIDDEN_MENU_LABEL_MAP[menu.menuLabel] ? false : !!menu.showMenu && !menu.hidden;
        }
    }
    return map;
};
if (inWindow()) {
    window.privilegeApi = {
        print() {
            return {
                menu: storageApi.getSync('wm_menu_setting').data,
                privilege: storageApi.getSync('wm_privileges_all').data,
            };
        },
        getState() { },
    };
}
